import { ReactFCC } from '@/types/react'
import { useTranslate } from '@/utils/translate/translate-client'
import { UploadErrorType } from './UploadErrorMessage'
import { UploadInactiveState, UploadPrompt, UploadProgress, UploadComplete } from './UploadStates'
import { formatSize } from './uploadHelpers'

interface UploadContentProps {
  maxSize: number
  isError: UploadErrorType | undefined
  isActive: boolean
  acknowledged: boolean
  setIsError: (error: UploadErrorType | undefined) => void
  file: File | null
  uploadProgress: number
  isPreparingUpload: boolean
  isUploadComplete: boolean
  handleAbort: () => void
  handleBrowseClick: () => void
  boxTitle: string
  isUploading: boolean
  acceptedFileType: 'image' | 'video' | 'text'
  type: 'APPLICATION' | 'UPDATE' | 'PROJECT'
}

export const UploadContent: ReactFCC<UploadContentProps> = ({
  maxSize,
  isError,
  isActive,
  acknowledged,
  setIsError,
  file,
  uploadProgress,
  isPreparingUpload,
  isUploadComplete,
  handleAbort,
  handleBrowseClick,
  boxTitle,
  isUploading,
  acceptedFileType,
  type,
}) => {
  const { t } = useTranslate('guild-create')
  const maxSizeText = maxSize ? t('maxSize', `Max size: {{ maxSize }}`, { maxSize: formatSize(maxSize) }) : ''
  const showErrorOrInactiveState = isError || !isActive || !acknowledged

  if (showErrorOrInactiveState) {
    return (
      <UploadInactiveState
        acknowledged={acknowledged ?? false}
        isActive={isActive ?? true}
        error={isError}
        onDismissError={() => setIsError(undefined)}
      />
    )
  }

  if (isUploading) {
    return (
      <UploadProgress
        uploadProgress={uploadProgress}
        isPreparingUpload={isPreparingUpload}
        file={file}
        onAbort={handleAbort}
      />
    )
  }

  if (isUploadComplete) {
    return <UploadComplete />
  }

  if (!file && isActive) {
    return (
      <UploadPrompt
        boxTitle={boxTitle}
        acceptedFileType={acceptedFileType}
        type={type}
        maxSizeText={maxSizeText}
        onBrowseClick={handleBrowseClick}
      />
    )
  }

  return null
}
